import React, { useState, useCallback, useRef } from 'react'
import { Button, Text, Icon, Flex, Box, Loader } from '@vibe/core'
import { Upload, Close, File, Alert, Check } from '@vibe/icons'
import { apiService } from '../../services/api'
import { VibeToast } from '../common/VibeToast'
import { getErrorMessage } from '../../utils/errorHandling'
import { useUploadPermission } from '../../hooks'
import type {
  UploadProgress,
  UploadResult,
  ValidationResult,
} from '../../types'

interface CVUploadProps {
  solicitudId: string
  onUploadComplete?: (result: UploadResult) => void
  onUploadProgress?: (progress: UploadProgress[]) => void
  onError?: (error: string) => void
  disabled?: boolean
  maxFiles?: number
  className?: string
  checkApprovalStatus?: boolean // New prop to enable/disable approval checking
  refreshInterval?: number // Interval for checking approval status updates
}

interface CVUploadToastState {
  message: string
  type: 'positive' | 'negative' | 'warning'
}

interface FileWithId extends File {
  id: string
}

interface FileUploadState {
  file: FileWithId
  status: 'pending' | 'uploading' | 'completed' | 'error'
  error?: string
}

// Simple status badge component since Badge is different in Vibe
const StatusBadge: React.FC<{
  status: 'pending' | 'uploading' | 'completed' | 'error'
  className?: string
}> = ({ status, className = '' }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800'
      case 'error':
        return 'bg-red-100 text-red-800'
      case 'uploading':
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = () => {
    switch (status) {
      case 'pending':
        return 'Listo'
      case 'uploading':
        return 'Subiendo'
      case 'completed':
        return 'Completado'
      case 'error':
        return 'Error'
    }
  }

  return (
    <span
      className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor()} ${className}`}
    >
      {getStatusText()}
    </span>
  )
}

export const CVUpload: React.FC<CVUploadProps> = ({
  solicitudId,
  onUploadComplete,
  onUploadProgress,
  onError,
  disabled = false,
  maxFiles = 10,
  className = '',
  checkApprovalStatus = true,
  refreshInterval = 30000, // 30 seconds default
}) => {
  const [isDragOver, setIsDragOver] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<FileUploadState[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null)
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [toast, setToast] = useState<CVUploadToastState | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Use upload permission hook
  const {
    permission: uploadPermission,
    loading: permissionLoading,
    error: permissionError,
    refetch: checkUploadPermission,
    isUploadAllowed,
  } = useUploadPermission({
    solicitudId,
    enabled: checkApprovalStatus,
    refreshInterval,
  })

  // Determine if component should be disabled
  const isComponentDisabled =
    disabled || (checkApprovalStatus && !isUploadAllowed)

  // Generate unique ID for files
  const generateFileId = () => Math.random().toString(36).substring(2, 11)

  // Show toast notification
  const showToast = useCallback(
    (message: string, type: 'positive' | 'negative' | 'warning') => {
      setToast({ message, type })
    },
    []
  )

  // Validate files before adding to selection
  const validateFiles = useCallback(
    (files: File[]): ValidationResult => {
      const validation = apiService.validateUploadFiles(files)

      // Additional validation for max files
      const totalFiles = selectedFiles.length + files.length
      if (totalFiles > maxFiles) {
        validation.errors.push(
          `Máximo ${maxFiles} archivos pueden ser subidos a la vez`
        )
        validation.isValid = false
      }

      return validation
    },
    [selectedFiles.length, maxFiles]
  )

  // Handle file selection (both drag-drop and file input)
  const handleFileSelection = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files)
      const validation = validateFiles(fileArray)

      if (!validation.isValid) {
        setValidationErrors(validation.errors)
        onError?.(validation.errors.join(', '))
        return
      }

      // Clear previous validation errors
      setValidationErrors([])

      // Add files to selection with unique IDs
      const newFiles: FileUploadState[] = fileArray.map(file => ({
        file: Object.assign(file, { id: generateFileId() }) as FileWithId,
        status: 'pending',
      }))

      setSelectedFiles(prev => [...prev, ...newFiles])
    },
    [validateFiles, onError]
  )

  // Drag and drop handlers
  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (!isComponentDisabled) {
        setIsDragOver(true)
      }
    },
    [isComponentDisabled]
  )

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragOver(false)

      if (isComponentDisabled) return

      const files = e.dataTransfer.files
      if (files.length > 0) {
        handleFileSelection(files)
      }
    },
    [isComponentDisabled, handleFileSelection]
  )

  // File input handler
  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (files && files.length > 0) {
        handleFileSelection(files)
      }
      // Reset input value to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [handleFileSelection]
  )

  // Remove file from selection
  const removeFile = useCallback((fileId: string) => {
    setSelectedFiles(prev => prev.filter(f => f.file.id !== fileId))
  }, [])

  // Clear all files
  const clearAllFiles = useCallback(() => {
    setSelectedFiles([])
    setValidationErrors([])
    setUploadResult(null)
  }, [])

  // Upload files
  const uploadFiles = useCallback(async () => {
    if (selectedFiles.length === 0 || isUploading) return

    setIsUploading(true)
    setValidationErrors([])

    try {
      const files = selectedFiles.map(f => f.file)

      // Update progress callback
      const progressCallback = (progress: UploadProgress[]) => {
        // Update local state
        setSelectedFiles(prev =>
          prev.map(fileState => {
            const progressItem = progress.find(
              p => p.filename === fileState.file.name
            )
            if (progressItem) {
              return {
                ...fileState,
                status: progressItem.status,
                error: progressItem.error,
              }
            }
            return fileState
          })
        )

        // Call external callback
        onUploadProgress?.(progress)
      }

      // Start upload
      const result = await apiService.uploadCVFiles(solicitudId, files)

      // Update final progress
      progressCallback(result.progress)

      setUploadResult(result)
      onUploadComplete?.(result)

      // Show success message if all uploads completed
      const summary = apiService.getUploadSummary(result.progress)
      if (summary.completed === summary.total) {
        showToast(
          `Se subieron exitosamente ${summary.completed} CV${summary.completed > 1 ? 's' : ''}`,
          'positive'
        )
      } else if (summary.error > 0) {
        showToast(
          `${summary.completed} subidos, ${summary.error} fallaron`,
          'warning'
        )
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error) || 'Error al subir archivos'
      setValidationErrors([errorMessage])
      onError?.(errorMessage)

      showToast(errorMessage, 'negative')
    } finally {
      setIsUploading(false)
    }
  }, [
    selectedFiles,
    isUploading,
    solicitudId,
    onUploadComplete,
    onUploadProgress,
    onError,
  ])

  // Retry failed uploads
  const retryFailedUploads = useCallback(async () => {
    const failedFiles = selectedFiles.filter(f => f.status === 'error')
    if (failedFiles.length === 0) return

    // Reset failed files to pending
    setSelectedFiles(prev =>
      prev.map(f =>
        f.status === 'error' ? { ...f, status: 'pending', error: undefined } : f
      )
    )

    // Retry upload
    await uploadFiles()
  }, [selectedFiles, uploadFiles])

  const hasErrors =
    selectedFiles.some(f => f.status === 'error') || validationErrors.length > 0
  const allCompleted =
    selectedFiles.length > 0 &&
    selectedFiles.every(f => f.status === 'completed')

  // Render approval status message
  const renderApprovalStatusMessage = () => {
    if (!checkApprovalStatus || permissionLoading) {
      return (
        <Box className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <Flex align="center">
            <Loader className="mr-2 w-5 h-5" />
            <Text type="text2" weight="medium">
              Verificando estado de aprobación...
            </Text>
          </Flex>
        </Box>
      )
    }

    if (permissionError) {
      return (
        <Box className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <Flex align="center" className="mb-2">
            <Icon icon={Alert} iconSize={16} className="text-red-500 mr-2" />
            <Text type="text2" weight="medium" className="text-red-700">
              Error al verificar permisos
            </Text>
          </Flex>
          <Text type="text3" className="text-red-600 mb-2">
            {permissionError}
          </Text>
          <Button size="small" kind="secondary" onClick={checkUploadPermission}>
            Reintentar
          </Button>
        </Box>
      )
    }

    if (uploadPermission && !uploadPermission.upload_allowed) {
      const isRejected = uploadPermission.approval_status === 'rejected'
      const isPending = uploadPermission.approval_status === 'pending'

      return (
        <Box
          className={`mb-4 p-4 border rounded-lg ${
            isRejected
              ? 'bg-red-50 border-red-200'
              : 'bg-yellow-50 border-yellow-200'
          }`}
        >
          <Flex align="center" className="mb-2">
            <Icon
              icon={Alert}
              iconSize={16}
              className={`mr-2 ${
                isRejected ? 'text-red-500' : 'text-yellow-600'
              }`}
            />
            <Text
              type="text2"
              weight="medium"
              className={isRejected ? 'text-red-700' : 'text-yellow-800'}
            >
              {isRejected ? 'Solicitud Rechazada' : 'Esperando Aprobación'}
            </Text>
          </Flex>

          <Text
            type="text3"
            className={`mb-3 ${
              isRejected ? 'text-red-600' : 'text-yellow-700'
            }`}
          >
            {uploadPermission.reason ||
              (isRejected
                ? 'Esta solicitud ha sido rechazada y no permite subir CVs.'
                : 'Esta solicitud requiere aprobación antes de permitir la subida de CVs.')}
          </Text>

          {isPending && uploadPermission.workflow_info && (
            <div className="text-sm text-yellow-700">
              <Text type="text3">
                Progreso:{' '}
                {Math.round(
                  uploadPermission.workflow_info.completion_percentage
                )}
                % completado
              </Text>
              <Text type="text3">
                Aprobaciones pendientes:{' '}
                {uploadPermission.workflow_info.pending_approvals}
              </Text>
            </div>
          )}

          {isPending && (
            <div className="mt-3 pt-3 border-t border-yellow-200">
              <Text type="text3" className="text-yellow-600">
                Los CVs se podrán subir automáticamente una vez que se complete
                el proceso de aprobación.
              </Text>
            </div>
          )}
        </Box>
      )
    }

    if (uploadPermission && uploadPermission.upload_allowed) {
      return (
        <Box className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <Flex align="center">
            <Icon icon={Check} iconSize={16} className="text-green-600 mr-2" />
            <Text type="text2" className="text-green-700">
              Solicitud aprobada - Subida de CVs habilitada
            </Text>
          </Flex>
        </Box>
      )
    }

    return null
  }

  return (
    <Box className={`cv-upload ${className}`}>
      {/* Approval Status Message */}
      {renderApprovalStatusMessage()}

      {/* Drag and Drop Area */}
      <div
        className={`
          border-2 border-dashed rounded-lg p-8 text-center transition-colors
          ${isDragOver ? 'border-primary-500 bg-primary-50' : 'border-gray-300'}
          ${isComponentDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary-400'}
        `}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => !isComponentDisabled && fileInputRef.current?.click()}
      >
        <Icon
          icon={isComponentDisabled ? Alert : Upload}
          iconSize={32}
          className={`mx-auto mb-4 ${
            isComponentDisabled ? 'text-gray-300' : 'text-gray-400'
          }`}
        />

        <Text type="text1" weight="medium" className="mb-2">
          {isComponentDisabled
            ? 'Subida de CVs no disponible'
            : isDragOver
              ? 'Suelta los archivos aquí'
              : 'Arrastra y suelta archivos de CV aquí'}
        </Text>

        {!isComponentDisabled && (
          <>
            <Text type="text2" className="text-gray-600 mb-4">
              o haz clic para buscar archivos
            </Text>

            <Text type="text3" className="text-gray-500">
              Solo archivos PDF, máximo 4.5MB cada uno, hasta {maxFiles}{' '}
              archivos
            </Text>
          </>
        )}

        {isComponentDisabled &&
          uploadPermission &&
          !uploadPermission.upload_allowed && (
            <Text type="text2" className="text-gray-500">
              {uploadPermission.approval_status === 'rejected'
                ? 'La solicitud ha sido rechazada'
                : 'Esperando aprobación de la solicitud'}
            </Text>
          )}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,application/pdf"
          onChange={handleFileInputChange}
          className="hidden"
          disabled={isComponentDisabled}
        />
      </div>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <Box className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <Flex align="center" className="mb-2">
            <Icon icon={Alert} iconSize={16} className="text-red-500 mr-2" />
            <Text type="text2" weight="medium" className="text-red-700">
              Errores de Validación
            </Text>
          </Flex>
          {validationErrors.map((error, index) => (
            <Text key={index} type="text3" className="text-red-600 ml-6">
              • {error}
            </Text>
          ))}
        </Box>
      )}

      {/* Selected Files List */}
      {selectedFiles.length > 0 && (
        <Box className="mt-6">
          <Flex justify="space-between" align="center" className="mb-4">
            <Text type="text2" weight="medium">
              Archivos Seleccionados ({selectedFiles.length})
            </Text>
            <Button
              size="small"
              kind="tertiary"
              onClick={clearAllFiles}
              disabled={isUploading || isComponentDisabled}
            >
              Limpiar Todo
            </Button>
          </Flex>

          <div className="space-y-3">
            {selectedFiles.map(fileState => (
              <div
                key={fileState.file.id}
                className="border border-gray-200 rounded-lg p-4"
              >
                <Flex justify="space-between" align="center" className="mb-2">
                  <Flex align="center">
                    <Icon
                      icon={File}
                      iconSize={16}
                      className="mr-2 text-gray-500"
                    />
                    <Text type="text2" className="truncate max-w-xs">
                      {fileState.file.name}
                    </Text>
                    <Text type="text3" className="text-gray-500 ml-2">
                      ({(fileState.file.size / 1024 / 1024).toFixed(1)} MB)
                    </Text>
                  </Flex>

                  <Flex align="center">
                    <StatusBadge status={fileState.status} className="mr-2" />

                    {!isUploading &&
                      !isComponentDisabled &&
                      fileState.status !== 'completed' && (
                        <Button
                          size="small"
                          kind="tertiary"
                          onClick={() => removeFile(fileState.file.id)}
                        >
                          <Icon icon={Close} iconSize={16} />
                        </Button>
                      )}
                  </Flex>
                </Flex>

                {/* Error Message */}
                {fileState.status === 'error' && fileState.error && (
                  <Text type="text3" className="text-red-600">
                    Error: {fileState.error}
                  </Text>
                )}
              </div>
            ))}
          </div>

          {/* Upload Status */}
          {isUploading && (
            <Box className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Flex align="center">
                <Loader className="mr-2 w-5 h-5" />
                <Text type="text2" weight="medium">
                  Subiendo archivos...
                </Text>
              </Flex>
            </Box>
          )}

          {/* Action Buttons */}
          <Flex justify="space-between" className="mt-4">
            <div>
              {hasErrors && !isUploading && !isComponentDisabled && (
                <Button
                  size="medium"
                  kind="secondary"
                  onClick={retryFailedUploads}
                  disabled={
                    selectedFiles.filter(f => f.status === 'error').length === 0
                  }
                >
                  Reintentar Fallidos
                </Button>
              )}
            </div>

            <Button
              size="medium"
              onClick={uploadFiles}
              disabled={
                isUploading ||
                selectedFiles.length === 0 ||
                allCompleted ||
                validationErrors.length > 0 ||
                isComponentDisabled
              }
            >
              {isComponentDisabled &&
              uploadPermission &&
              !uploadPermission.upload_allowed
                ? uploadPermission.approval_status === 'rejected'
                  ? 'Subida No Permitida'
                  : 'Esperando Aprobación'
                : isUploading
                  ? 'Subiendo...'
                  : allCompleted
                    ? 'Todos Subidos'
                    : `Subir ${selectedFiles.length} Archivo${selectedFiles.length > 1 ? 's' : ''}`}
            </Button>
          </Flex>

          {/* Upload Summary */}
          {uploadResult && (
            <Box className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <Text type="text2" weight="medium" className="mb-2">
                Resumen de Subida
              </Text>
              {(() => {
                const summary = apiService.getUploadSummary(
                  uploadResult.progress
                )
                return (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>Total de Archivos: {summary.total}</div>
                    <div>Completados: {summary.completed}</div>
                    <div>Fallidos: {summary.error}</div>
                    <div>
                      Tasa de Éxito:{' '}
                      {Math.round((summary.completed / summary.total) * 100)}%
                    </div>
                  </div>
                )
              })()}

              {uploadResult.completed && (
                <Text type="text3" className="text-green-600 mt-2">
                  ✓ {uploadResult.completed.postulaciones.length} registros de
                  candidatos creados
                </Text>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Toast Notification */}
      <VibeToast
        open={!!toast}
        type={toast?.type || 'positive'}
        message={toast?.message || ''}
        onClose={() => setToast(null)}
        autoHideDuration={5000}
        className="fixed top-4 right-4 z-50"
      />
    </Box>
  )
}

export default CVUpload
